import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';
import React, {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {EHeaderButton} from 'webapp-lib/Components';
import {classy} from 'webapp-lib/utils';

import {SubHeading} from '#';
import {SubTabBar, SubTabItem} from '#/SubTabBar/SubTabBar';
import {TransparentBackground} from '#/TransparentBackground';
import {Spinner, Upload} from '_/assets/images';
import {I18NextNS} from '_/i18n';
import {DocumentUploadContext} from '_/utilities';

import {UploadQueueSideBar} from './UploadQueue.styles';
import {UploadQueueLists} from './UploadQueueLists';

type UploadQueueTabItem = {
  value: 'PENDING' | 'SUCCESSFUL' | 'FAILED';
};

const UUploadQueue = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}): React.ReactElement | null => {
  const {t} = useTranslation<I18NextNS>('documents');
  const documentUploadStore = useContext(DocumentUploadContext);

  const tabItems: SubTabItem<UploadQueueTabItem>[] = [
    {
      id: 'pending',
      value: 'PENDING',
      text: t('uploadQueue.pending', {
        amount: documentUploadStore.documentFileUploads.pending.length,
      }),
    },
    {
      id: 'successful',
      value: 'SUCCESSFUL',
      text: t('uploadQueue.successful', {
        amount: documentUploadStore.documentFileUploads.successful.length,
      }),
    },
    {
      id: 'failed',
      value: 'FAILED',
      text: t('uploadQueue.failed', {
        amount: documentUploadStore.documentFileUploads.failed.length,
      }),
      mode: 'important',
    },
  ];
  const [activeTabItem, setActiveTabItem] = useState(tabItems[0]);

  const uploadInProgress =
    documentUploadStore.documentFileUploads.pending.length > 0;

  return (
    <StyledUploadQueue
      className={classy({
        'upload-in-progress': uploadInProgress,
      })}
    >
      <EHeaderButton
        contentFlag={
          documentUploadStore.documentFileUploads.failed.length > 0
            ? {
                content: documentUploadStore.documentFileUploads.failed.length,
                mode: 'warning',
              }
            : undefined
        }
        isActive={isOpen}
        title={t('common:headerButtons.uploadQueue')}
        onClick={() => setIsOpen(!isOpen)}
      >
        {uploadInProgress ? <Spinner /> : <Upload />}
      </EHeaderButton>
      {isOpen ? (
        <>
          <TransparentBackground onMouseUp={() => setIsOpen(false)} />
          <UploadQueueSideBar>
            <SubHeading>{t('uploadQueue.title')}</SubHeading>
            <SubTabBar
              activeTabItem={activeTabItem}
              items={tabItems}
              fullWidth
              onChange={setActiveTabItem}
            />
            <UploadQueueLists
              abortAllPendingFiles={documentUploadStore.abortAllPendingUploads}
              activeTabItem={activeTabItem.value}
              clearAllFailedFiles={documentUploadStore.clearAllFailedUploads}
              clearAllSuccessfulFiles={
                documentUploadStore.clearAllSuccessfulUploads
              }
              closePopup={() => setIsOpen(false)}
              items={documentUploadStore.documentFileUploads}
              retryUpload={documentUploadStore.retryUpload}
            />
          </UploadQueueSideBar>
        </>
      ) : null}
    </StyledUploadQueue>
  );
};

export const UploadQueue = observer(UUploadQueue);

const StyledUploadQueue = styled.div`
  height: 100%;

  &.upload-in-progress {
    .EHeaderButton .WrapperEIconButton button:not(:hover) {
      background-color: var(--color-tag-todo);

      svg {
        color: var(--color-core-primary);
      }
    }
  }
`;
